import {Box, Stack, Typography, useTheme} from '@mui/material';
import {ErrorState, TagChip} from 'presentation/components';
import {Drawer} from 'presentation/components/Drawer';
import {Timeline} from 'presentation/components/Timeline';
import {translations} from 'presentation/translations-new';
import {textHandle} from 'presentation/utils/textHandle';
import React, {Fragment, useState} from 'react';
import {cardStyleByOdontoStatus, OdontoStatus} from '../../constants';
import {useAuthOdontoStatusDrawerData} from './hooks';
import {AuthOdontoDetailLoading} from './Loading';
import {AuthOdontoStatusDrawerProps} from './types';

const {details, close, lastUpdateOdonto, failToLoadTitle, failToLoadDescription} =
	translations['pt-br'].commons;

export function AuthOdontoStatusDrawer({
	authorization,
	openDrawer,
	onOpenDrawer,
	onCloseDrawer,
	shouldShowErrorState = false,
}: AuthOdontoStatusDrawerProps) {
	const [hasRefetched, setHasRefetched] = useState(false);
	const theme = useTheme();
	const {chipText, chipVariant} = cardStyleByOdontoStatus(
		theme,
		authorization.dsStatusItemAutor as OdontoStatus,
	);

	const {timelineData, lastUpdate, isLoading, isError, refetch, shouldShowContent} =
		useAuthOdontoStatusDrawerData({
			authorizationCode: parseInt(authorization.cdAutorizacao, 10),
			procedureCode: authorization.cdProcedimento,
			procedureKeyCode: authorization.nuChvProcedimento,
			shouldSkipFetch: shouldShowErrorState && !hasRefetched,
		});

	const handleRefetch = () => {
		setHasRefetched(true);
		refetch();
	};

	const showInitialErrorState = shouldShowErrorState && !hasRefetched;

	return (
		<Drawer.Root
			open={openDrawer}
			onClose={onCloseDrawer}
			onOpen={onOpenDrawer}
			width={'396px'}
			customMaxHeight="90%">
			<Drawer.Header onClose={onCloseDrawer} title={details} />

			{showInitialErrorState ? (
				<Box flex={1} pb={4}>
					<ErrorState
						title={failToLoadTitle}
						description={failToLoadDescription}
						handleRefresh={handleRefetch}
					/>
				</Box>
			) : null}

			{isError && !showInitialErrorState ? (
				<Box flex={1} pb={4}>
					<ErrorState
						title={failToLoadTitle}
						description={failToLoadDescription}
						handleRefresh={refetch}
					/>
				</Box>
			) : null}

			{isLoading ? <AuthOdontoDetailLoading /> : null}

			{shouldShowContent && !showInitialErrorState ? (
				<Drawer.Content>
					<Fragment>
						<Stack>
							<Typography variant="h6" mb={2} fontWeight={400} color="primary">
								{textHandle(authorization?.nmProcedimento).capitalize()}
							</Typography>
							<TagChip
								label={chipText}
								color={chipVariant}
								sx={{
									width: 'fit-content',
								}}
							/>
							<Typography mt={1} mb={2} variant="body2" color="text.primary">
								{lastUpdateOdonto}
								{lastUpdate}
							</Typography>
						</Stack>
						<Timeline content={timelineData} customPaddingBottom />
					</Fragment>
				</Drawer.Content>
			) : null}

			<Drawer.Footer
				secondButtonTitle={close}
				secondButtonOnClick={onCloseDrawer}
				hideFirstButton
			/>
		</Drawer.Root>
	);
}
